/**
 * Authentication API Service
 * 
 * Handles all authentication-related API calls
 * Production-ready with proper error handling
 */

import apiClient from './client';

/**
 * User registration
 * @param {Object} data - Registration data
 * @param {string} data.email - User email
 * @param {string} data.password - User password
 * @param {string} data.fullName - Full name
 * @param {string} data.organization - Organization/institution
 * @returns {Promise<Object>} User data
 */
export const register = async (data) => {
    try {
        const response = await apiClient.post('/api/v1/auth/register', {
            email: data.email,
            password: data.password,
            full_name: data.fullName || data.full_name,
            organization: data.organization || data.institution?.value || data.otherInstitution,
            country: data.country?.value || data.country,
            phone_number: data.phoneNumber || data.phone_number,
            referral_code: data.referralCode || undefined,
            // The signup form has always required this tick before it will
            // submit; it just was never sent, so nothing recorded that the
            // assessor accepted. The backend stamps terms_accepted_at from it.
            terms_accepted: data.termsAccepted === true,
            turnstile_token: data.turnstileToken || undefined,
        });

        // Registration logs the user straight in — the real tokens arrive as
        // HttpOnly cookies (issue #196), set by this response already;
        // there's nothing left for this JS to store.
        return response.data;
    } catch (error) {
        // Enhance error message for common registration errors
        if (error.statusCode === 400) {
            throw new Error(error.message || 'Invalid registration data');
        }
        if (error.message?.includes('already exists')) {
            throw new Error('An account with this email already exists');
        }
        throw error;
    }
};

/**
 * User login
 * @param {Object} credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Promise<Object>} Authentication tokens and user data
 */
export const login = async (credentials) => {
    try {
        const response = await apiClient.post('/api/v1/auth/login', {
            email: credentials.email,
            password: credentials.password,
            turnstile_token: credentials.turnstileToken || undefined,
        });

        const { user } = response.data;

        // The real tokens arrived as HttpOnly cookies (issue #196), set by
        // this response already. Only the non-sensitive user object is
        // cached here — for fast UI paint, and as the "am I logged in"
        // signal AuthContext checks before bothering to call /me.
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        }

        return response.data;
    } catch (error) {
        // Enhance error messages
        if (error.statusCode === 401) {
            throw new Error('Invalid email or password');
        }
        if (error.statusCode === 429) {
            throw new Error('Too many login attempts. Please try again later.');
        }
        throw error;
    }
};

/**
 * Exchange a one-time OAuth handoff code (from the /auth-callback?code=...
 * redirect) for the real access + refresh tokens. The backend no longer
 * puts real tokens in that redirect URL — a browser's actual request for
 * that URL happens before any of this JS runs, exposing them to every
 * access log along the way — so this POST is how the real tokens are
 * actually retrieved, in a response body, never a URL. Does NOT store
 * tokens itself (matches refreshToken's behavior below); the caller does
 * that after checking the response.
 * @param {string} code - One-time code from the redirect
 * @returns {Promise<Object>} {access_token, refresh_token, ...}
 */
export const exchangeOAuthCode = async (code) => {
    const response = await apiClient.post('/api/v1/auth/oauth/exchange', { code });
    return response.data;
};

/**
 * Refresh access token. The refresh token itself lives in an HttpOnly
 * cookie (issue #196) — apiClient's own 401 interceptor is what actually
 * drives this in practice; this export exists for a caller that wants to
 * force a refresh explicitly.
 * @returns {Promise<Object>} New token metadata
 */
export const refreshToken = async () => {
    try {
        const response = await apiClient.post('/api/v1/auth/refresh', {});
        return response.data;
    } catch (error) {
        // If refresh fails, user needs to re-login
        logout();
        throw new Error('Session expired. Please login again.');
    }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getCurrentUser = async () => {
    try {
        const response = await apiClient.get('/api/v1/auth/me');

        // Update cached user data
        localStorage.setItem('user', JSON.stringify(response.data));

        return response.data;
    } catch (error) {
        console.error('Failed to fetch user profile:', error);
        throw error;
    }
};

/**
 * Update user profile
 * @param {Object} updates - Profile updates
 * @returns {Promise<Object>} Updated user data
 */
export const updateProfile = async (updates) => {
    try {
        const response = await apiClient.put('/api/v1/auth/me', updates);

        // Update cached user data
        localStorage.setItem('user', JSON.stringify(response.data));

        return response.data;
    } catch (error) {
        console.error('Failed to update profile:', error);
        throw error;
    }
};

/**
 * Complete profile for Google OAuth users
 * @param {Object} profileData - Profile completion data
 * @returns {Promise<Object>} Updated user data
 */
export const completeProfile = async (profileData) => {
    try {
        const response = await apiClient.post('/api/v1/auth/complete-profile', profileData);
        // Update cached user data
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
    } catch (error) {
        console.error('Failed to complete profile:', error);
        throw error;
    }
};

/**
 * Change password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Success message
 */
export const changePassword = async (currentPassword, newPassword) => {
    try {
        const response = await apiClient.post('/api/v1/auth/change-password', {
            current_password: currentPassword,
            new_password: newPassword,
        });

        return response.data;
    } catch (error) {
        if (error.statusCode === 400) {
            throw new Error('Current password is incorrect');
        }
        throw error;
    }
};

/**
 * Logout user.
 * Issue #196: the tokens are HttpOnly cookies now — this JS can't clear
 * them itself the way it used to clear localStorage. The backend call
 * revokes the session server-side and sends back Set-Cookie headers that
 * delete them; only the non-sensitive local UI cache is cleared here.
 */
export const logout = async () => {
    try {
        await apiClient.post('/api/v1/auth/logout');
    } catch (error) {
        // Still proceed to clear local state and redirect even if the
        // network call failed — an unreachable backend shouldn't strand
        // the user on a page that thinks they're logged in.
        console.error('Logout request failed:', error);
    }

    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Redirect to login
    window.location.href = '/login';
};

/**
 * Check if user is authenticated. This is a UI hint only (the cached user
 * object, not a token) — every real request is still authenticated by the
 * HttpOnly cookie the server checks, not by anything read here.
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
    return !!getCachedUser();
};

/**
 * Get cached user data
 * @returns {Object|null} Cached user data or null
 */
export const getCachedUser = () => {
    try {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
        console.error('Error parsing cached user:', error);
        return null;
    }
};

// Export all functions as a service object
const authService = {
    register,
    login,
    refreshToken,
    getCurrentUser,
    updateProfile,
    completeProfile,
    changePassword,
    logout,
    isAuthenticated,
    getCachedUser,
};

export default authService;
